'use client'

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { BookingWidgetContext } from '@tebuto/widget/booking-widget/context'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BookingCalendarContext } from './context'
import { isFullSelection, uniqueNumberIds } from './selection-utils'

type FilterItemId = number | string

type FilterDropdownProps<T extends FilterItemId> = {
    label: string
    allLabel: string
    items: { id: T; name: string }[]
    selectedIds: T[]
    allSelected: boolean
    onToggle: (id: T) => void
    onSelectAll: () => void
    dataTestId?: string
    allOptionTestId?: string
    optionTestIdPrefix?: string
}

function FilterDropdown<T extends FilterItemId>({
    label,
    allLabel,
    items,
    selectedIds,
    allSelected,
    onToggle,
    onSelectAll,
    dataTestId,
    allOptionTestId,
    optionTestIdPrefix
}: FilterDropdownProps<T>) {
    const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const displayText = useMemo(() => {
        if (selectedIds.length === 0 || allSelected) {
            return allLabel
        }
        if (selectedIds.length === 1) {
            const item = items.find((i) => i.id === selectedIds[0])
            return item?.name || allLabel
        }
        return `${selectedIds.length} ${label}`
    }, [selectedIds, items, allLabel, label, allSelected])

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!open) return

        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    return (
        <div className="relative w-full sm:flex-1 sm:min-w-0">
            <button
                ref={buttonRef}
                type="button"
                data-testid={dataTestId}
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 text-xs widget-bg-field border widget-border rounded-md widget-text-primary hover:widget-bg-muted transition-colors"
                style={{
                    outline: 'none',
                    boxShadow: 'none',
                    borderWidth: '1px'
                }}
            >
                <span className="truncate">{displayText}</span>
                <ChevronDownIcon
                    className={`size-3.5 widget-text-secondary shrink-0 transition-transform ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {open && (
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 right-0 mt-1 widget-bg-primary border widget-border shadow-sm rounded-md p-1.5 z-50 max-h-60 overflow-y-auto"
                >
                    <div className="space-y-0.5">
                        {/* All option */}
                        <button
                            type="button"
                            data-testid={allOptionTestId}
                            onClick={() => {
                                onSelectAll()
                                setOpen(false)
                            }}
                            className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors focus:outline-none ${
                                allSelected
                                    ? 'widget-bg-primary-100 widget-text-primary-color-700 font-medium'
                                    : 'widget-text-primary hover:widget-bg-muted'
                            }`}
                        >
                            {allLabel}
                        </button>

                        <div className="border-t widget-border my-1" />

                        {/* Individual items */}
                        {items.map((item) => {
                            const isSelected = selectedIds.includes(item.id)
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    data-testid={optionTestIdPrefix ? `${optionTestIdPrefix}-${item.id}` : undefined}
                                    onClick={() => onToggle(item.id)}
                                    className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-2 focus:outline-none ${
                                        isSelected && !allSelected
                                            ? 'widget-bg-primary-100 widget-text-primary-color-700 font-medium'
                                            : 'widget-text-primary hover:widget-bg-muted'
                                    }`}
                                >
                                    <span
                                        className={`size-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                                            isSelected
                                                ? 'widget-bg-primary-500 widget-border-primary-500'
                                                : 'widget-border'
                                        }`}
                                    >
                                        {isSelected && (
                                            <svg
                                                className="size-2.5 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={3}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="truncate">{item.name}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function QuickFilters() {
    const { t } = useComponentTranslation()
    const {
        categories,
        therapists,
        availableCategoryIDs,
        selectedCategoryIDs,
        setSelectedCategoryIDs,
        availableTherapistIDs,
        selectedTherapistIDs,
        setSelectedTherapistIDs,
        selectedCities,
        setSelectedCities,
        availableCities,
        showQuickFilters,
        showLocationQuickFilter,
        isMultiUser
    } = useContext(BookingCalendarContext)
    const { configuredCategories, configuredTherapists } = useContext(BookingWidgetContext)

    // Use configured categories if provided (from step 1), otherwise derive from events
    const sortedCategories = useMemo(() => {
        if (configuredCategories && configuredCategories.length > 0) {
            const availableCategoryIdSet = new Set(availableCategoryIDs)
            const seen = new Set<number>()
            return [...configuredCategories]
                .filter((category) => availableCategoryIdSet.has(category.id))
                .filter((category) => {
                    if (seen.has(category.id)) {
                        return false
                    }
                    seen.add(category.id)
                    return true
                })
                .sort((a, b) => a.name.localeCompare(b.name))
        }
        const uniqueCategoryIds = [...new Set(availableCategoryIDs)]
        return uniqueCategoryIds
            .map((id) => categories[id])
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [configuredCategories, availableCategoryIDs, categories])

    // Use configured therapists if provided (main + subusers), otherwise derive from events
    const sortedTherapists = useMemo(() => {
        const availableTherapistIdSet = new Set(availableTherapistIDs)
        if (configuredTherapists && configuredTherapists.length > 0) {
            const seen = new Set<number>()
            return [...configuredTherapists]
                .filter((therapist) => availableTherapistIdSet.has(therapist.id))
                .filter((therapist) => {
                    if (seen.has(therapist.id)) {
                        return false
                    }
                    seen.add(therapist.id)
                    return true
                })
                .sort((a, b) => a.name.localeCompare(b.name))
        }
        const uniqueTherapistIds = [...new Set(availableTherapistIDs)]
        return uniqueTherapistIds
            .map((id) => therapists[id])
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [configuredTherapists, availableTherapistIDs, therapists])

    const cityItems = useMemo(
        () =>
            availableCities.map((city) => ({
                id: city,
                name: city
            })),
        [availableCities]
    )

    const effectiveCategoryIDs = useMemo(() => [...new Set(availableCategoryIDs)], [availableCategoryIDs])

    const allTherapistsSelected = isFullSelection(selectedTherapistIDs, availableTherapistIDs)

    const toggleCategory = (categoryId: number) => {
        setSelectedCategoryIDs((prevSelected) => {
            const allSelected = prevSelected.length === effectiveCategoryIDs.length

            // If all categories are selected, clicking one should select ONLY that one
            if (allSelected) {
                return [categoryId]
            }

            if (prevSelected.includes(categoryId)) {
                // Don't allow deselecting if it's the last one
                if (prevSelected.length > 1) {
                    return prevSelected.filter((id) => id !== categoryId)
                }
                return prevSelected
            }
            return [...prevSelected, categoryId]
        })
    }

    const selectAllCategories = () => {
        setSelectedCategoryIDs(effectiveCategoryIDs)
    }

    const toggleTherapist = (therapistId: number) => {
        setSelectedTherapistIDs((prevSelected) => {
            const allSelected = isFullSelection(prevSelected, availableTherapistIDs)

            if (allSelected) {
                return [therapistId]
            }

            if (prevSelected.includes(therapistId)) {
                if (prevSelected.length > 1) {
                    return prevSelected.filter((id) => id !== therapistId)
                }
                return prevSelected
            }
            return [...prevSelected, therapistId]
        })
    }

    const selectAllTherapists = () => {
        setSelectedTherapistIDs(uniqueNumberIds(availableTherapistIDs))
    }

    const toggleCity = (city: string) => {
        setSelectedCities((prevSelected) => {
            const allSelected = prevSelected.length === availableCities.length

            if (allSelected) {
                return [city]
            }

            if (prevSelected.includes(city)) {
                if (prevSelected.length > 1) {
                    return prevSelected.filter((selectedCity) => selectedCity !== city)
                }
                return prevSelected
            }

            return [...prevSelected, city]
        })
    }

    const selectAllCities = () => {
        setSelectedCities(availableCities)
    }

    const showCategoryFilter = showQuickFilters && sortedCategories.length > 1

    const showTherapistFilter = showQuickFilters && isMultiUser && sortedTherapists.length > 1

    const showLocationFilter = showLocationQuickFilter && cityItems.length > 1

    if (!showCategoryFilter && !showTherapistFilter && !showLocationFilter) {
        return null
    }

    return (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
            {showLocationFilter && (
                <FilterDropdown
                    label={t('booking.bookingCalendar.place')}
                    allLabel={t('booking.bookingCalendar.allPlaces')}
                    items={cityItems}
                    selectedIds={selectedCities}
                    allSelected={selectedCities.length === availableCities.length}
                    onToggle={toggleCity}
                    onSelectAll={selectAllCities}
                />
            )}

            {showCategoryFilter && (
                <FilterDropdown
                    label={t('booking.bookingCalendar.categories')}
                    allLabel={t('booking.bookingCalendar.allCategories')}
                    items={sortedCategories}
                    selectedIds={selectedCategoryIDs}
                    allSelected={selectedCategoryIDs.length === effectiveCategoryIDs.length}
                    onToggle={toggleCategory}
                    onSelectAll={selectAllCategories}
                />
            )}

            {showTherapistFilter && (
                <FilterDropdown
                    label={t('booking.bookingCalendar.therapists')}
                    allLabel={t('booking.bookingCalendar.allTherapists')}
                    items={sortedTherapists}
                    selectedIds={selectedTherapistIDs}
                    allSelected={allTherapistsSelected}
                    onToggle={toggleTherapist}
                    onSelectAll={selectAllTherapists}
                    dataTestId="widget-therapist-quick-filter"
                    allOptionTestId="widget-therapist-quick-filter-all"
                    optionTestIdPrefix="widget-therapist-quick-filter-option"
                />
            )}
        </div>
    )
}
